import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2 } from 'lucide-react';
import CreatableCombobox from '../CreatableCombobox';
import useCsmCatalogue from '../../hooks/useCsmCatalogue';

const title = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// CSM-style unit details: spec fields (per style/manufacturer), driver + driver specs, accessories.
export default function UnitSpecFields({ form, set }) {
  const csm = useCsmCatalogue();
  const specFields = csm.specFields(form.style, form.make);
  const seriesOptions = csm.seriesOptions(form.style, form.make);
  const driverFields = csm.driverFields(form.driver_type);
  const accTypes = csm.accessoryTypes(form.style);
  const specs = form.specifications || {};
  const dspecs = form.driver_specifications || {};
  const accessories = form.accessories || [];

  const setAcc = (i, patch) => set({ accessories: accessories.map((a, j) => (j === i ? { ...a, ...patch } : a)) });

  return (
    <div className="space-y-4" data-testid="unit-spec-fields">
      {(specFields.length > 0 || seriesOptions.length > 0) && (
        <div>
          <p className="text-xs font-bold uppercase text-slate-500 mb-2">Specifications</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Frame designation / series</Label>
              <CreatableCombobox value={form.series || ''} options={seriesOptions} onChange={(series) => set({ series })} noun="series" placeholder="Select or type" testid="unit-series" />
            </div>
            {specFields.map((f) => (
              <div key={f.label} className="space-y-1.5">
                <Label>{title(f.label)}</Label>
                <CreatableCombobox value={specs[f.label] || ''} options={f.options} onChange={(v) => set({ specifications: { ...specs, [f.label]: v } })}
                  noun={f.label.toLowerCase()} placeholder="Select or type" testid={`unit-spec-${f.label.toLowerCase().replace(/\s+/g, '-')}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-bold uppercase text-slate-500 mb-2">Driver</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Driver type</Label>
            <Select value={form.driver_type || 'none'} onValueChange={(v) => set({ driver_type: v === 'none' ? '' : v, driver_specifications: {} })}>
              <SelectTrigger data-testid="unit-driver-type"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="z-[130]">
                <SelectItem value="none" data-testid="unit-driver-none">None</SelectItem>
                {form.driver_type && !csm.driverTypes.includes(form.driver_type) && <SelectItem value={form.driver_type} data-testid="unit-driver-legacy">{title(form.driver_type)}</SelectItem>}
                {csm.driverTypes.map((d) => <SelectItem key={d} value={d} data-testid={`unit-driver-option-${d.toLowerCase().replace(/\s+/g, '-')}`}>{title(d)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {driverFields.map((f) => (
            <div key={f.label} className="space-y-1.5">
              <Label>{title(f.label)}</Label>
              <CreatableCombobox value={dspecs[f.label] || ''} options={f.options} onChange={(v) => set({ driver_specifications: { ...dspecs, [f.label]: v } })}
                noun={f.label.toLowerCase()} placeholder="Select or type" testid={`unit-driver-${f.label.toLowerCase().replace(/\s+/g, '-')}`} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold uppercase text-slate-500">Accessories <span className="font-normal normal-case text-slate-400">({accessories.length}/20)</span></p>
          <Button type="button" size="sm" variant="outline" disabled={accessories.length >= 20} onClick={() => set({ accessories: [...accessories, { type: '', manufacturer: '', model: '', size: '' }] })} data-testid="unit-accessory-add-btn">
            <Plus size={13} className="mr-1" /> Add accessory
          </Button>
        </div>
        {accessories.map((a, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-[1.3fr_1.2fr_1fr_0.8fr_auto] gap-2 items-center mb-2" data-testid={`unit-accessory-${i}`}>
            <CreatableCombobox value={a.type} options={accTypes} onChange={(type) => setAcc(i, { type, manufacturer: '' })} noun="accessory" placeholder="Accessory" testid={`unit-accessory-type-${i}`} />
            <CreatableCombobox value={a.manufacturer} options={csm.accessoryManufacturers(form.style, a.type)} onChange={(manufacturer) => setAcc(i, { manufacturer })} noun="manufacturer" placeholder="Manufacturer" testid={`unit-accessory-mfr-${i}`} />
            <Input value={a.model} onChange={(e) => setAcc(i, { model: e.target.value })} placeholder="Model" className="h-9" data-testid={`unit-accessory-model-${i}`} />
            <Input value={a.size} onChange={(e) => setAcc(i, { size: e.target.value })} placeholder="Size" className="h-9" data-testid={`unit-accessory-size-${i}`} />
            <button type="button" onClick={() => set({ accessories: accessories.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600 p-1" aria-label="Remove accessory" data-testid={`unit-accessory-remove-${i}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
